// Data adapter to bridge frontend and backend interfaces
import type { TrainingProgram as BackendTrainingProgram, WorkoutSession as BackendWorkoutSession, Set as BackendSet, Exercise } from './types';

// Frontend interfaces (keeping existing structure for compatibility)
export interface FrontendTrainingProgram {
  id: number;
  name: string;
  weekStartDate: Date;
  weekEndDate: Date;
  workoutSessions: FrontendWorkoutSession[];
  userIri?: string;
  // Backend fields for editing
  description?: string;
  introduction?: string;
  durationWeeks: number;
  workoutsPerWeek: number;
  difficulty: string;
  goal: string;
  active: boolean;
  createdAt: Date;
  updatedAt?: Date;
}

export interface FrontendWorkoutSession {
  id: number;
  name: string;
  date: Date;
  sets: FrontendSet[];
  // Backend fields
  notes?: string;
  durationMinutes?: number;
  status: 'planned' | 'in_progress' | 'completed' | 'skipped';
}

export interface FrontendSet {
  id: number;
  exercise: { id: number; name: string; muscleGroup?: string };
  setNumber: number;
  weightKg: number;
  reps: number;
  // Additional backend fields
  notes?: string;
  restSeconds?: number;
  durationSeconds?: number;
}

export class DataAdapter {
  // Convert backend training program to frontend format
  static toFrontendTrainingProgram(
    backendProgram: BackendTrainingProgram, 
    workoutSessions: BackendWorkoutSession[] = [],
    sets: BackendSet[] = [],
    exercises: Exercise[] = []
  ): FrontendTrainingProgram {
    // Calculate week dates based on durationWeeks and current date
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + (backendProgram.durationWeeks * 7) - 1);

    // Group sessions and sets
    const sessionsWithSets = workoutSessions.map(session => {
      const sessionSets = sets.filter(set => 
        set.workoutSession === `/api/workout_sessions/${session.id}`
      );
      return this.toFrontendWorkoutSession(session, sessionSets, exercises);
    });

    return {
      id: backendProgram.id,
      name: backendProgram.name,
      weekStartDate: startDate,
      weekEndDate: endDate,
      workoutSessions: sessionsWithSets,
      userIri: backendProgram.user,
      description: backendProgram.description,
      introduction: backendProgram.introduction,
      durationWeeks: backendProgram.durationWeeks,
      workoutsPerWeek: backendProgram.workoutsPerWeek,
      difficulty: backendProgram.difficulty,
      goal: backendProgram.goal,
      active: backendProgram.active,
      createdAt: new Date(backendProgram.createdAt),
      updatedAt: backendProgram.updatedAt ? new Date(backendProgram.updatedAt) : undefined,
    };
  }

  // Convert backend workout session to frontend format
  static toFrontendWorkoutSession(
    backendSession: BackendWorkoutSession, 
    sets: BackendSet[] = [],
    exercises: Exercise[] = []
  ): FrontendWorkoutSession {
    const sessionSets = sets.map(set => this.toFrontendSet(set, exercises));

    return {
      id: backendSession.id,
      name: backendSession.name,
      date: new Date(backendSession.workoutDate),
      sets: sessionSets,
      notes: backendSession.notes,
      durationMinutes: backendSession.durationMinutes,
      status: backendSession.status,
    };
  }

  // Convert backend set to frontend format
  static toFrontendSet(backendSet: BackendSet, exercises: Exercise[] = []): FrontendSet {
    // Extract exercise ID from IRI
    const exerciseId = parseInt(backendSet.exercise.split('/').pop() || '0');
    const exercise = exercises.find(ex => ex.id === exerciseId) || {
      id: exerciseId,
      name: 'Unknown Exercise',
      muscleGroup: 'Unknown'
    };

    return {
      id: backendSet.id,
      exercise: {
        id: exercise.id,
        name: exercise.name,
        muscleGroup: exercise.muscleGroup
      },
      setNumber: backendSet.setOrder,
      weightKg: typeof backendSet.weight === 'number' ? backendSet.weight : (parseFloat(backendSet.weight as string) || 0),
      reps: backendSet.reps,
      notes: backendSet.notes,
      restSeconds: backendSet.restSeconds,
      durationSeconds: backendSet.durationSeconds,
    };
  }

  // Convert frontend to backend format for creating/updating
  static toBackendTrainingProgram(frontendProgram: Partial<FrontendTrainingProgram>) {
    return {
      name: frontendProgram.name || '',
      description: frontendProgram.description,
      introduction: frontendProgram.introduction,
      durationWeeks: frontendProgram.durationWeeks || 1,
      workoutsPerWeek: frontendProgram.workoutsPerWeek || 3,
      difficulty: frontendProgram.difficulty || 'beginner',
      goal: frontendProgram.goal || 'general_fitness',
      active: frontendProgram.active ?? false,
    };
  }

  static toBackendWorkoutSession(frontendSession: Partial<FrontendWorkoutSession>, programIri?: string) {
    return {
      name: frontendSession.name || '',
      notes: frontendSession.notes,
      workoutDate: frontendSession.date?.toISOString() || new Date().toISOString(),
      durationMinutes: frontendSession.durationMinutes,
      status: frontendSession.status || 'planned' as const,
      trainingProgram: programIri,
    };
  }

  static toBackendSet(frontendSet: Partial<FrontendSet>, workoutSessionIri: string, exerciseIri: string) {
    return {
      reps: frontendSet.reps || 0,
      weight: frontendSet.weightKg,
      durationSeconds: frontendSet.durationSeconds,
      notes: frontendSet.notes,
      restSeconds: frontendSet.restSeconds,
      setOrder: frontendSet.setNumber || 1,
      exercise: exerciseIri,
      workoutSession: workoutSessionIri,
    };
  }
}
